import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { authAPI, usersAPI } from '../services/api';
import { clearAdminPermissionsCache } from '../hooks/useAdminPermissions';
import { needsSocialProfileChoice } from '../utils/oauth';
import {
  normalizeSessionUser,
  readStoredAuthUser,
  isKnownRole,
} from '../utils/sessionUser';

const AuthContext = createContext(null);

function unwrapAuthPayload(data) {
  if (!data || typeof data !== 'object') return {};
  if (data.access_token || data.token) return data;
  if (data.data && typeof data.data === 'object') return data.data;
  return data;
}

function persistSessionUser(user, setUser) {
  if (!user?.id || !isKnownRole(user.role)) {
    return false;
  }
  localStorage.setItem('user', JSON.stringify(user));
  flushSync(() => setUser(user));
  return true;
}

function applyAuthSessionPayload(data, setUser) {
  const payload = unwrapAuthPayload(data);
  const access_token = payload.access_token || payload.token;
  const refresh_token = payload.refresh_token || payload.refresh;
  const rawUser = payload.user;
  if (!access_token || !refresh_token || !rawUser || typeof rawUser !== 'object') {
    return false;
  }
  localStorage.setItem('access_token', access_token);
  localStorage.setItem('refresh_token', refresh_token);
  const user = normalizeSessionUser(rawUser);
  return persistSessionUser(user, setUser);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    try {
      const { data } = await usersAPI.getProfile();
      const normalized = normalizeSessionUser(data);
      if (!persistSessionUser(normalized, setUser)) {
        return null;
      }
      return normalized;
    } catch (err) {
      if (err?.response?.status === 401) {
        authAPI.logout();
        flushSync(() => setUser(null));
      }
      return null;
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const storedUser = readStoredAuthUser();
      const token = localStorage.getItem('access_token');
      if (storedUser && token) {
        flushSync(() => setUser(storedUser));
        await refreshUser();
      }
      setLoading(false);
    };
    init();
  }, [refreshUser]);

  const adminLogin = async (credentials) => {
    try {
      const response = await authAPI.adminLogin(credentials);
      if (!response.data) {
        throw new Error('Resposta inválida do servidor');
      }
      if (!applyAuthSessionPayload(response.data, setUser)) {
        throw new Error(
          'Resposta de login incompleta. Atualize a página ou tente novamente em instantes.'
        );
      }
      const sessionUser = await refreshUser();
      return { success: true, user: sessionUser || unwrapAuthPayload(response.data).user };
    } catch (error) {
      const d = error.response?.data?.detail;
      const errorMessage =
        typeof d === 'string' ? d : d?.message || error.message || 'Erro ao fazer login';
      throw new Error(errorMessage);
    }
  };

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      if (!response.data) {
        throw new Error('Resposta inválida do servidor');
      }
      if (!applyAuthSessionPayload(response.data, setUser)) {
        throw new Error(
          'Resposta de login incompleta. Atualize a página ou tente novamente em instantes.'
        );
      }
      const sessionUser = await refreshUser();
      return { success: true, user: sessionUser || unwrapAuthPayload(response.data).user };
    } catch (error) {
      const d = error.response?.data?.detail;
      if (
        error.response?.status === 403 &&
        d &&
        typeof d === 'object' &&
        !Array.isArray(d) &&
        d.code === 'EMAIL_NOT_VERIFIED'
      ) {
        const e = new Error(d.message || 'Confirme o seu e-mail.');
        e.code = 'EMAIL_NOT_VERIFIED';
        e.email = credentials.email;
        throw e;
      }
      const errorMessage =
        typeof d === 'string' ? d : d?.message || error.message || 'Erro ao fazer login';
      throw new Error(errorMessage);
    }
  };

  const signup = async (userData) => {
    try {
      const { data } = await authAPI.signup(userData);
      if (data.access_token && data.refresh_token && data.user) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        const user = normalizeSessionUser(data.user);
        persistSessionUser(user, setUser);
        await refreshUser();
        return { success: true, user };
      }
      return {
        success: true,
        pendingVerification: true,
        message: data.message,
        emailMasked: data.email_masked,
        email: userData.email,
      };
    } catch (error) {
      const d = error.response?.data?.detail;
      throw new Error(typeof d === 'string' ? d : d?.message || 'Erro ao criar conta');
    }
  };

  const completeEmailVerification = useCallback(
    async (token) => {
      const trimmed = (token || '').trim();
      if (!trimmed) {
        throw new Error('Link inválido: falta o token de confirmação.');
      }
      try {
        const { data } = await authAPI.verifyEmail(trimmed);
        if (!applyAuthSessionPayload(data, setUser)) {
          throw new Error(
            'Resposta de login incompleta. Atualize a página ou tente novamente em instantes.'
          );
        }
        await refreshUser();
        return { success: true, user: data.user };
      } catch (error) {
        const d = error.response?.data?.detail;
        const msg =
          typeof d === 'string'
            ? d
            : d?.message || error.message || 'Não foi possível confirmar o e-mail.';
        throw new Error(msg);
      }
    },
    [refreshUser]
  );

  const logout = () => {
    authAPI.logout();
    clearAdminPermissionsCache();
    setUser(null);
  };

  const completeOAuthSession = useCallback(
    async (data) => {
      if (!applyAuthSessionPayload(data, setUser)) {
        throw new Error(
          'Resposta de login incompleta. Atualize a página ou tente novamente em instantes.'
        );
      }
      const sessionUser = await refreshUser();
      const fallbackUser = normalizeSessionUser(unwrapAuthPayload(data).user);
      const user = sessionUser || (isKnownRole(fallbackUser.role) ? fallbackUser : null);
      if (user && !sessionUser) {
        persistSessionUser(user, setUser);
      }
      return { user, needsProfileChoice: needsSocialProfileChoice(user) };
    },
    [refreshUser]
  );

  const googleSignIn = useCallback(
    async (idToken, picture = null) => {
      const { data } = await authAPI.googleSignIn(idToken, 'client', picture);
      return completeOAuthSession(data);
    },
    [completeOAuthSession]
  );

  const appleSignIn = useCallback(
    async (payload) => {
      const { data } = await authAPI.appleSignIn(payload);
      return completeOAuthSession(data);
    },
    [completeOAuthSession]
  );

  const updateUserLocal = (partial) => {
    setUser((prev) => {
      const next = normalizeSessionUser({ ...prev, ...partial }, prev);
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  const isAdmin = user?.role === 'admin';
  const isClient = user?.role === 'client';
  const isVideomaker = user?.role === 'videomaker';
  const isAuthenticated = Boolean(user?.id && isKnownRole(user?.role));

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        adminLogin,
        signup,
        completeEmailVerification,
        completeOAuthSession,
        googleSignIn,
        appleSignIn,
        logout,
        refreshUser,
        updateUserLocal,
        isAdmin,
        isClient,
        isVideomaker,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
